import { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from "recharts";

const API_URL = process.env.REACT_APP_API_URL;

function fmtDate(s) {
  if (!s) return "—";
  return new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function daysUntil(s) {
  if (!s) return null;
  return Math.ceil((new Date(s) - new Date()) / 86400000);
}

const EpsTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{ background: "#1a1a2e", border: "1px solid #2a2a4a", borderRadius: 8, padding: "8px 12px", fontSize: 12 }}>
      <div style={{ color: "#8b8ba7", marginBottom: 4 }}>{d.period}</div>
      <div style={{ color: "#e2e8f0" }}>Actual: <strong style={{ color: d.actual >= 0 ? "#10b981" : "#ef4444" }}>${d.actual?.toFixed(2) ?? "—"}</strong></div>
      {d.estimate != null && <div style={{ color: "#8b8ba7" }}>Estimate: ${d.estimate.toFixed(2)}</div>}
      {d.actual != null && d.estimate != null && (
        <div style={{ color: d.beat ? "#10b981" : "#ef4444", marginTop: 2 }}>
          {d.beat ? "▲ Beat" : "▼ Missed"} by ${Math.abs(d.actual - d.estimate).toFixed(2)}
        </div>
      )}
    </div>
  );
};

export default function EarningsPanel({ ticker }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    fetch(`${API_URL}/api/seo/${ticker}/earnings`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError("Earnings data unavailable"); setLoading(false); } });
    return () => { cancelled = true; };
  }, [ticker]);

  const earnings = data?.earnings || [];
  const nextDate = data?.nextEarningsDate;
  const daysAway = daysUntil(nextDate);

  const chartData = earnings.slice(0, 4).reverse().map((e) => ({
    period: e.period || "—",
    actual: e.actual,
    estimate: e.estimate,
    beat: e.actual != null && e.estimate != null ? e.actual >= e.estimate : null,
  }));

  return (
    <div style={{ background: "#0f0f23", border: "1px solid #1e1e3a", borderRadius: 12, padding: "20px 24px", marginBottom: 24 }}>
      <h3 style={{ margin: "0 0 16px", color: "#e2e8f0", fontSize: 15, fontWeight: 600 }}>Earnings</h3>

      {loading && <div style={{ color: "#8b8ba7", fontSize: 14, padding: "16px 0" }}>Loading earnings…</div>}
      {!loading && error && <div style={{ color: "#8b8ba7", fontSize: 14 }}>{error}</div>}

      {!loading && !error && (
        <>
          {nextDate && (
            <div style={{ background: "#1a1a2e", borderRadius: 8, padding: "10px 14px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 18 }}>📅</span>
              <div>
                <div style={{ fontSize: 12, color: "#8b8ba7" }}>Next earnings report</div>
                <div style={{ fontSize: 14, color: "#e2e8f0", fontWeight: 600 }}>
                  {fmtDate(nextDate)}
                  {daysAway != null && daysAway >= 0 && (
                    <span style={{ color: "#6366f1", marginLeft: 8, fontWeight: 400 }}>in {daysAway} day{daysAway !== 1 ? "s" : ""}</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {chartData.length > 0 ? (
            <>
              <div style={{ fontSize: 13, color: "#8b8ba7", marginBottom: 10 }}>EPS — Actual vs Estimate (last 4 quarters)</div>
              <div style={{ height: 180 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} barGap={2} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                    <XAxis dataKey="period" tick={{ fill: "#8b8ba7", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#8b8ba7", fontSize: 11 }} axisLine={false} tickLine={false} width={48}
                      tickFormatter={(v) => `$${v.toFixed(2)}`} />
                    <Tooltip content={<EpsTip />} />
                    <ReferenceLine y={0} stroke="#2a2a4a" strokeDasharray="3 3" />
                    <Bar dataKey="estimate" fill="#2a2a4a" radius={[3, 3, 0, 0]} barSize={14} />
                    <Bar dataKey="actual"   radius={[3, 3, 0, 0]} barSize={14}>
                      {chartData.map((e, i) => (
                        <Cell key={i} fill={e.actual == null ? "#4b4b6a" : e.actual >= 0 ? "#10b981" : "#ef4444"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
                {[["#10b981","Actual (positive)"],["#ef4444","Actual (negative)"],["#2a2a4a","Estimate"]].map(([c,l]) => (
                  <div key={l} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#8b8ba7" }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: c, display: "inline-block" }} />{l}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ color: "#8b8ba7", fontSize: 14 }}>No earnings history available.</div>
          )}
        </>
      )}
    </div>
  );
}
